// XSD validation via xmllint-wasm running in a CLASSIC worker (Firefox-safe, D-020). The
// worker source is the patched vendored xmllint-browser worker (import.meta.url → self.location.href),
// staged at /engines/xmllint-browser-classic.js by prepare-assets; it resolves xmllint.wasm
// relative to its own location (also under /engines/).

import { loadXsdBundle, xmllintWorkerUrl } from './assets';
import type { Syntax } from './types';

const MAIN_XSD: Record<Syntax, string> = {
  UBL: 'UBL-Invoice-2.1.xsd',
  CII: 'CrossIndustryInvoice_100pD16B.xsd',
};

export interface XsdResult {
  valid: boolean;
  errors: string[];
}

export async function validateXsd(xmlText: string, syntax: Syntax): Promise<XsdResult> {
  const bundle = await loadXsdBundle(syntax);
  const mainName = MAIN_XSD[syntax];
  const main = bundle.find((f) => f.fileName === mainName);
  if (!main) throw new Error(`main XSD ${mainName} missing from ${syntax} bundle`);
  const preload = bundle.filter((f) => f.fileName !== mainName);

  return new Promise<XsdResult>((resolve, reject) => {
    const worker = new Worker(xmllintWorkerUrl());
    worker.addEventListener('message', (event: MessageEvent) => {
      const { exitCode, stderr } = event.data as { exitCode: number; stderr: string };
      worker.terminate();
      if (exitCode === 0) resolve({ valid: true, errors: [] });
      // xmllint exit codes 3 (schema) / 4 (validation) == XSD-invalid, with parser errors on stderr.
      else if (exitCode === 3 || exitCode === 4) resolve({ valid: false, errors: splitErrors(stderr) });
      else reject(new Error(stderr || `xmllint exited ${exitCode}`));
    });
    worker.addEventListener('error', (err) => {
      worker.terminate();
      reject(err instanceof Error ? err : new Error(String((err as ErrorEvent).message ?? err)));
    });
    worker.postMessage({
      inputFiles: [
        { fileName: 'invoice.xml', contents: xmlText },
        { fileName: main.fileName, contents: main.contents },
        ...preload,
      ],
      args: ['--schema', mainName, '--noout', 'invoice.xml'],
      initialMemory: 512,
      maxMemory: 4096,
    });
  });
}

function splitErrors(stderr: string): string[] {
  return (stderr || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}
